// Shared React Query client — Wave 1 (perf): stale-while-revalidate caching.
//
// Behavior contract:
//   - Revisiting a page renders the CACHED data instantly, then refetches in the
//     background (staleTime below controls how long data is treated as fresh).
//   - Cache invalidation is CENTRAL, not per-component: the api.js response
//     interceptor calls invalidateForRequest() after every successful mutating
//     request (POST/PUT/PATCH/DELETE). Any write that can change compliance
//     data — evidence upload/delete, control/requirement changes, policy
//     finalize, connector sync, NC changes, vault approvals, framework links,
//     risk edits — invalidates the affected query families, so active screens
//     refetch immediately and inactive ones refetch on next mount. The user
//     never acts on stale posture after their own write.
//   - Query keys are ALWAYS tenant-scoped: ['dashboard', tenantId, ...] /
//     ['controls', tenantId, ...]. Workspace switches therefore never show
//     another tenant's cached rows, and logout/session-expiry clears the whole
//     cache (AuthContext calls clearQueryCache()).
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Fresh for 30s (no refetch storm when hopping between pages);
            // after that: cached render + silent background refetch.
            staleTime: 30 * 1000,
            // Keep unused data 30 min so deep back-navigation stays instant.
            gcTime: 30 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

// URL-pattern → query-key families to invalidate. A pattern matching the
// request path invalidates every listed family (all tenants — keys include
// tenantId, and invalidation is prefix-based, so this is safe and simple).
const INVALIDATION_RULES = [
    // Evidence / requirements → posture + controls
    { pattern: /\/(evidence|requirements)/i, keys: [['dashboard'], ['controls']] },
    // Vault documents (upload/approve/renew/archive) → vault lists + posture
    { pattern: /\/(vault|documents)/i, keys: [['dashboard'], ['controls'], ['vault']] },
    // Policy edits/finalize → policy library, vault copy, posture
    { pattern: /\/(policies|policy)/i, keys: [['dashboard'], ['controls'], ['policies'], ['vault']] },
    // Control + framework changes (status toggles, links, seeding, gap runs)
    { pattern: /\/(controls|frameworks|soa)/i, keys: [['dashboard'], ['controls']] },
    // Connector sync / findings state changes (also refreshes automated tests)
    { pattern: /\/connectors/i, keys: [['dashboard'], ['controls'], ['tests']] },
    // Automated / org tests
    { pattern: /\/(automated-tests|org-tests)/i, keys: [['dashboard'], ['controls'], ['tests']] },
    // Nonconformities affect control status
    { pattern: /\/(nc|nonconformit)/i, keys: [['dashboard'], ['controls']] },
    // Risk register writes → register list + dashboard risk heatmap tile
    { pattern: /\/risk/i, keys: [['dashboard'], ['risks']] },
    // Vendor writes → vendor list + KPI tiles
    { pattern: /\/vendors/i, keys: [['vendors']] },
    // Auditor evidence requests → Action Required tile
    { pattern: /\/(audit-requests|auditor)/i, keys: [['dashboard']] },
    // User admin changes → vault approver/owner pickers
    { pattern: /\/users/i, keys: [['vault']] },
];

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

// Auth + chat endpoints churn constantly and never change compliance data.
const IGNORE = /\/(auth|login|logout|refresh|agent|copilot|chat|traces)\b/i;

/** Called by the api.js response interceptor after every successful request.
 *  Invalidates cached query families affected by a mutating request. */
export function invalidateForRequest(method, url) {
    if (!MUTATING.has(String(method || '').toLowerCase())) return;
    const u = String(url || '');
    if (IGNORE.test(u)) return;
    const hit = new Set();
    for (const rule of INVALIDATION_RULES) {
        if (rule.pattern.test(u)) rule.keys.forEach((k) => hit.add(JSON.stringify(k)));
    }
    hit.forEach((k) => queryClient.invalidateQueries({ queryKey: JSON.parse(k) }));
}

/** Full cache wipe — logout / session expiry (never leak across users). */
export function clearQueryCache() {
    queryClient.clear();
}
